import { useCallback } from 'react';

import { t as translate } from '../lib/i18n';
import { useLocaleStore } from '../store/useLocaleStore';

/**
 * React hook for translating UI strings.
 *
 * Usage:
 * ```tsx
 * const { t, locale, setLocale, availableLocales } = useTranslation();
 * return <h1>{t('flow.welcome.title')}</h1>;
 * ```
 */
export function useTranslation() {
  const locale = useLocaleStore((s) => s.locale);
  const setLocale = useLocaleStore((s) => s.setLocale);
  const availableLocales = useLocaleStore((s) => s.availableLocales);
  const bundlesVersion = useLocaleStore((s) => s.bundlesVersion);

  // Wrap `translate` in a stable callback that depends on `locale` and on the
  // bundle version. When the locale changes — or a bundle is overlaid at
  // runtime (site-copy, #662) — React re-renders components that call `t()`.
  const t = useCallback(
    (key: string, values?: Record<string, string | number>) => translate(key, values),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locale, bundlesVersion],
  );

  return { t, locale, setLocale, availableLocales } as const;
}
